/* eslint-disable import/order, import/no-extraneous-dependencies, @typescript-eslint/no-unused-vars */
// 1. The import order of macros matter and they must be kept in this order
// 2. Since macros are transpiled out during build, it is okay for them
//   to be imported even when they are not used.
// -- color must always be first -- //
import color from '@haaretz/l-color.macro';
// ---
import fontStack from '@haaretz/l-font-stack.macro';
import radius from '@haaretz/l-radius.macro';
import space from '@haaretz/l-space.macro';
import zIndex from '@haaretz/l-z-index.macro';
// --- These return objects and must be spread or used inside `merge` --- //
import border from '@haaretz/l-border.macro';
import shadow from '@haaretz/l-shadow.macro';
import typesetter from '@haaretz/l-type.macro';
// --- These must come last --- //
import fork from '@haaretz/l-fork.macro';
import mq from '@haaretz/l-mq.macro';
import merge from '@haaretz/l-merge.macro';
/* eslint-enable import/order, import/no-extraneous-dependencies, @typescript-eslint/no-unused-vars */

import * as React from 'react';
import s9 from 'style9';

import type { StyleExtend, InlineStyles } from '@haaretz/s-types';

// `c` is short for `classNames`
const c = s9.create({
  base: {
    listStyleType: 'none',
    paddingInlineStart: space(4),
    position: 'relative',

    '::before': {
      position: 'absolute',
      insetInlineStart: 0,
      top: 0,
      content: 'var(--shape)',
      fontSize: 'var(--size)',
      color: 'var(--htz-lnk-c)',
      fontWeight: 700,

      ...merge(
        mq({ from: 'l', until: 'xxl', value: { marginInlineEnd: space(2) } }),
        mq({ from: 'xxl', value: { marginInlineEnd: space(2.5) } })
      ),
    },
  },
  circleLargeStyleType: {
    paddingInlineStart: space(5),

    ...typesetter(1),
    ...merge(
      mq({
        from: 's',
        until: 'xxl',
        value: {
          paddingInlineStart: space(7),
        },
      }),
      mq({
        from: 'xxl',
        value: {
          paddingInlineStart: space(8),
          ...typesetter(0),
        },
      })
    ),
    '::before': {
      content: '""',
      top: space(2.25),
      width: space(2),
      height: space(2),
      borderRadius: radius('circle'),
      backgroundColor: 'var(--htz-lnk-c)',
      ...merge(
        mq({
          from: 's',
          value: {
            top: space(1.75),
            width: space(3),
            height: space(3),
          },
        }),
        mq({
          from: 'xl',
          value: {
            top: space(2),
          },
        })
      ),
    },
  },
  circleStyleType: {
    '--shape': '"•"',
    '--size': space(5.25),
  },
  triangleStyleType: {
    '--shape': `"${fork({ default: '◀', hdc: '▶' })}"`,
    '--size': space(3),
  },
  brandVariant: {
    '--htz-lnk-c': color('primary800'),
  },
  primaryVariant: {
    '--htz-lnk-c': color('primary1100'),
  },
  neutralVariant: {
    '--htz-lnk-c': color('neutral1000'),
  },
  dangerVariant: {
    '--htz-lnk-c': color('secondary900'),
  },
  successVariant: {
    '--htz-lnk-c': color('quaternary700'),
  },
  salesVariant: {
    '--htz-lnk-c': color('sales500'),
  },
  liveBlogVariant: {
    '--htz-lnk-c': color('textLightAllModes'),

    ...merge(
      mq({
        from: 'l',
        value: {
          '--htz-lnk-c': color('neutral1200'),
        },
      })
    ),
  },
});

export interface HtzLiProps {
  /** The Children to be rendered inside `<HtzLi>`, parent must be '<ul>' */
  children?: React.ReactNode;
  /**
   * CSS declarations to be set as inline `style` on the
   * html element.
   *
   * By setting values of CSS Custom Properties based on
   * props or state in the consuming component (where
   * the value of `inlineStyle` is passed), `inlineStyle`
   * can be used as an API contract for setting dynamic
   * values to styles created with `style9.create()`:
   *
   * @example
   * ```ts
   * import s9 from 'style9';
   * const { styleExtend, } = s9.create({
   *   styleExtend: {
   *     color: 'var(--color-based-on-prop)',
   *   },
   * });
   *
   * function MyHtzLi(props) {
   *   const inlineStyle = {
   *     '--color-based-on-prop': props.color,
   *   },
   *
   *   return (
   *    <HtzLi
   *      styleExtend={[ styleExtend, ]}
   *      inlineStyle={inlineStyle}
   *    />
   *   );
   * }
   * ```
   */
  inlineStyle?: InlineStyles;
  /**
   * An array of `Style`s created by `style9.create()`.
   * WARNING: **_do not_** pass simple CSS-in-JS object.
   * The items in the array must be created with Style9's
   * `create` function.
   * The array can also hold falsy values to assist with
   * conditional inclusion of `Style`s:
   *
   * @example
   * ```ts
   * const { foo, bar, } = s9.create({ foo: { ... }, bar: { ... }, });
   * <HtzLi styleExtend={[ someCondition && foo, bar, ]} />
   * ```
   */
  styleExtend?: StyleExtend;
  /**
   * Sets the color of the bullet
   *
   * @defaultValue 'circle'
   */
  styleType?: 'circle' | 'triangle';
  /**
   * Sets the color of the bullet
   *
   * @defaultValue 'brand'
   */
  variant?: 'brand' | 'neutral' | 'danger' | 'success' | 'sales' | 'primary' | 'liveBlog';
  size?: 'normal' | 'large' | 'medium';
}

export default function HtzLi({
  children = null,
  styleExtend = [],
  inlineStyle,
  variant = 'brand',
  styleType = 'circle',
  size = 'normal',
}: HtzLiProps) {
  const variantClass: `${HtzLiProps['variant']}Variant` = `${variant}Variant`;
  const shape: `${HtzLiProps['styleType']}StyleType` = `${styleType}StyleType`;

  return (
    <li
      className={s9(
        c.base,
        c[shape],
        styleType === 'circle' && size === 'large' && c.circleLargeStyleType,
        c[variantClass],
        ...styleExtend
      )}
      style={inlineStyle}
    >
      {children}
    </li>
  );
}
